import React, { useContext, useRef, useEffect, useState } from 'react';
import usePostSideFilters from "../../../hooks/home/usePostSideFilters";
import { Language } from '../../../App';
import {
  MdOutlineDynamicFeed,
  MdOutlineArticle,
  MdOutlinePoll,
  MdOutlineSensors,
  MdOutlineMedicalServices
} from "react-icons/md";

/* ── per-type accent config ────────────────────────────── */
const TYPE_CONFIG = {
  all:            { color: "#00BADA", light: "#E8FBFF", border: "#BCEEFA" },
  posts:          { color: "#3B82F6", light: "#EFF6FF", border: "#BFDBFE" },
  poll:           { color: "#F59E0B", light: "#FFFBEB", border: "#FDE68A" },
  webinar:        { color: "#EF4444", light: "#FEF2F2", border: "#FECACA" },
  "clinical-image": { color: "#10B981", light: "#ECFDF5", border: "#A7F3D0" },
};

const getConfig = (itemType) => TYPE_CONFIG[itemType] || TYPE_CONFIG.all;

const ICON_MAP = {
  all:              MdOutlineDynamicFeed,
  posts:            MdOutlineArticle,
  poll:             MdOutlinePoll,
  webinar:          MdOutlineSensors,
  "clinical-image": MdOutlineMedicalServices,
};

const PostSideFilters = ({ type, typesData, handleType }) => {
  const { lang } = useContext(Language);
  const itemRefs = useRef({});
  const [indicatorStyle, setIndicatorStyle] = useState({ top: 0, height: 0 });

  useEffect(() => {
    const timer = setTimeout(() => {
      const activeItem = itemRefs.current[type];
      if (activeItem) {
        setIndicatorStyle({
          top: activeItem.offsetTop,
          height: activeItem.offsetHeight,
        });
      }
    }, 0);
    return () => clearTimeout(timer);
  }, [type]);

  return (
    <>
      <style>{`
        .psf-card {
          background: linear-gradient(180deg, #FFFFFF 0%, #F8FDFF 100%);
          border-radius: 24px;
          border: 1px solid #D7E0EA;
          box-shadow: 0 8px 30px rgba(0,186,218,0.05), 0 2px 8px rgba(0,0,0,0.03);
          overflow: hidden;
          transition: box-shadow 0.25s ease, border-color 0.25s ease;
          font-family: 'Onest', sans-serif;
          position: relative;
        }
        .psf-card:hover {
          box-shadow: 0 12px 36px rgba(0,186,218,0.10), 0 4px 12px rgba(0,0,0,0.04);
          border-color: #B8E8F2;
        }
        .psf-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 20px;
          cursor: pointer;
          border: none;
          background: transparent;
          width: 100%;
          text-align: left;
          transition: background 0.15s ease;
          position: relative;
          outline: none;
        }
        .psf-item:hover {
          background: #F7FDFF;
        }
        .psf-item:hover .psf-icon-wrap {
          transform: scale(1.08);
        }
        .psf-item:hover .psf-label {
          color: #0F172A !important;
        }
        .psf-icon-wrap {
          width: 34px;
          height: 34px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.18s ease, background 0.18s ease;
        }
        .psf-label {
          font-size: 13.5px;
          font-weight: 500;
          font-family: 'Onest', sans-serif;
          transition: color 0.15s ease;
          line-height: 1.3;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        /* sliding indicator */
        .psf-indicator {
          position: absolute;
          left: 0;
          width: 3px;
          border-radius: 0 3px 3px 0;
          background: #00BADA;
          transition: transform 0.3s cubic-bezier(0.4,0,0.2,1), height 0.3s cubic-bezier(0.4,0,0.2,1);
          z-index: 2;
        }
      `}</style>
      <div className="filter-d-none" style={{ position: "relative", minWidth: "275px" }}>

        {/* ── Ambient glow ── */}
        <div
          style={{
            position: "absolute",
            top: "-20px", right: "-20px",
            width: "90px", height: "90px",
            background: "#00BADA",
            opacity: 0.08,
            filter: "blur(35px)",
            borderRadius: "50%",
            pointerEvents: "none",
            zIndex: 0,
          }}
        />
        <div className="psf-card">
          {/* Header */}
          <div
            className="d-flex align-items-center gap-2 px-4 py-3"
            style={{
              borderBottom: "1px solid #F1F5F9",
              position: "relative",
              zIndex: 2,
            }}
          >
            <div
              className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
              style={{ width: 28, height: 28, background: "#E8FBFF", color: "#00BADA", border: "1px solid #BCEEFA" }}
            >
              <MdOutlineDynamicFeed size={15} />
            </div>
            <h4 className="fs-15 roboto-bold mb-0" style={{ color: "#0F1A2B", lineHeight: 1.2 }}>
              {lang?.on_this_page || "On this page"}
            </h4>
          </div>

          {/* Filter list */}
          <ul className="list-unstyled mb-0" style={{ position: "relative" }}>
            {/* Animated left indicator bar */}
            <div
              className="psf-indicator"
              style={{
                height: `${indicatorStyle.height}px`,
                transform: `translateY(${indicatorStyle.top}px)`,
              }}
            />

            {typesData.map(({ text, type: itemType }) => {
              const isActive = itemType === type;
              const label = lang?.[text] || text.replace("_", " ");
              const cfg = getConfig(itemType);
              const IconEl = ICON_MAP[itemType] || MdOutlineDynamicFeed;

              return (
                <li key={itemType}>
                  <button
                    ref={(el) => (itemRefs.current[itemType] = el)}
                    onClick={() => handleType(itemType)}
                    onKeyDown={(e) => e.key === "Enter" && handleType(itemType)}
                    className="psf-item"
                    style={{
                      background: isActive ? cfg.light : "transparent",
                    }}
                    role="button"
                    tabIndex={0}
                    aria-label={`Filter by ${label}`}
                    aria-current={isActive ? "true" : undefined}
                  >
                    {/* Icon badge */}
                    <div
                      className="psf-icon-wrap"
                      style={{
                        background: isActive ? cfg.light : "#F1F5F9",
                        border: `1px solid ${isActive ? cfg.border : "transparent"}`,
                      }}
                    >
                      <IconEl
                        size={17}
                        style={{ color: isActive ? cfg.color : "#64748B" }}
                      />
                    </div>

                    {/* Label */}
                    <span
                      className="psf-label"
                      style={{
                        color: isActive ? cfg.color : "#475569",
                        fontWeight: isActive ? 700 : 500,
                      }}
                    >
                      {label}
                    </span>

                    {/* Active dot */}
                    {isActive && (
                      <div
                        style={{
                          marginLeft: "auto",
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          background: cfg.color,
                          flexShrink: 0,
                          boxShadow: `0 0 6px ${cfg.color}88`,
                        }}
                      />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </>
  );
};

const MemoizedPostSideFilters = React.memo(PostSideFilters);

// Wrapper Component
const PostSideFiltersWrapper = () => {
  const postSideFiltersProps = usePostSideFilters();
  return <MemoizedPostSideFilters {...postSideFiltersProps} />;
};

export default PostSideFiltersWrapper;